import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {API_BASE_URI} from '../http/api';
import {StorageRegime} from '@core/data/types/storage-regime';
import {Collection} from '../http/collection';
import {Filter, FilterBuilder} from '../http/filter';
import {RecentChange} from '../types/storage-regime';
import {map} from 'rxjs/operators';

@Injectable()
export class StorageRegimeService {

    constructor(
        private _http: HttpClient,
        @Inject(API_BASE_URI) private _baseUri: string,
    ) {
    }

    // Unified, org-scoped selection: Legacy Library Regimes (isGlobal) plus
    // the requesting org's own Custom Regimes, in one call.
    // With a crop, only the regimes that apply to it: the org's linked custom regimes and the matching legacy regimes.
    list(orgKey: string, cropId?: string): Observable<StorageRegime[]> {
        const url = `${this._baseUri}/organizations/${orgKey}/storage-regimes`;

        return cropId
            ? this._http.get<StorageRegime[]>(url, { params: { crop: cropId } })
            : this._http.get<StorageRegime[]>(url);
    }

    // Paginated, sorted, searchable listing of this org's own Custom Regimes only — backs the
    // Storage Regimes index table. Deliberately separate from list() above: that one returns
    // Legacy Library Regimes too, unpaginated, still needed by the regime-builder's "start from a
    // Legacy Regime" selector.
    queryCustom(orgKey: string, filter: Filter): Observable<Collection<StorageRegime>> {
        const params = (new FilterBuilder(filter)).getQueryParams();

        return this._http.get<Collection<StorageRegime>>(`${this._baseUri}/organizations/${orgKey}/storage-regimes/custom`, { params });
    }

    read(key: string): Observable<StorageRegime> {
        return this._http.get<StorageRegime>(`${this._baseUri}/storage-regimes/${key}`);
    }

    // isGlobal is never sent — the server rejects it outright (422) on both
    // create and update; only the one-time Legacy Library porting migration
    // may ever produce a global row.
    create(regime: Partial<StorageRegime>): Observable<StorageRegime> {
        return this._http.post<StorageRegime>(`${this._baseUri}/storage-regimes`, regime);
    }

    update(key: string, regime: Partial<StorageRegime>): Observable<StorageRegime> {
        return this._http.put<StorageRegime>(`${this._baseUri}/storage-regimes/${key}`, regime);
    }

    delete(key: string): Observable<StorageRegime> {
        return this._http.delete<StorageRegime>(`${this._baseUri}/storage-regimes/${key}`);
    }

    // Same filter body as exportChecklist(), so both views share one sample scope.
    listRecentChanges(orgKey: string, filter: Filter): Observable<RecentChange[]> {
        const body = FilterBuilder.queryParams(filter);

        return this._http.post<{ rows: RecentChange[] }>(`${this._baseUri}/organizations/${orgKey}/storage-regimes/recent-changes`, body)
            .pipe(map(response => response.rows));
    }

    // filter scopes the export to whatever samples it currently matches —
    // sent as a POST body (flattened via FilterBuilder.queryParams(), same
    // shape/precedent as SampleService.export()), since a sample filter can
    // be richer than comfortably fits a GET query string. isoWeek/isoYear
    // are nullable — null means no date constraint at all. timezone is the
    // browser-detected IANA zone; falls back to the Organization's own
    // configured timezone server-side if omitted.
    exportChecklist(orgKey: string, filter: Filter, isoWeek: number | null, isoYear: number | null, timezone: string): Observable<Blob> {
        const body = {
            ...FilterBuilder.queryParams(filter),
            isoWeek,
            isoYear,
            timezone,
        };

        return this._http.post<Blob>(`${this._baseUri}/organizations/${orgKey}/storage-regimes/checklist`, body, {
            responseType: 'blob' as 'json',
        });
    }
}
